import * as API from '../../api'
import { useCallback, useState } from 'react'
import { AxiosError } from 'axios'
import { useMutation } from '@tanstack/react-query'
import {
  Blockchain,
  useMintManifoldSignableTransaction,
  useMintSignableTransaction,
  useMintArtblocksSignableTransaction,
  useMintHighlightSignableTransaction,
  useMintHighlightChooseSignableTransaction,
  useConnectWallet,
} from '@verisart/nft/src'
import assert, { fail } from 'assert'
import { MintSignature } from '../../api'
import { Address, getAddress } from 'viem'

interface ErrorData {
  message?: string
}

const isErrorData = (data: unknown): data is ErrorData => {
  return typeof data === 'object'
}

const useSignedTransaction = () => {
  const { mutateAsync: mintManifoldSignableTransaction } =
    useMintManifoldSignableTransaction()
  const { mutateAsync: mintHighlightChooseSignableTransaction } =
    useMintHighlightChooseSignableTransaction()
  const { mutateAsync: mintHighlightSignableTransaction } =
    useMintHighlightSignableTransaction()
  const { mutateAsync: mintArtblocksSignableTransaction } =
    useMintArtblocksSignableTransaction()
  const { mutateAsync: mintSignableTransaction } = useMintSignableTransaction()

  return useMutation({
    mutationFn: async (mintSignature: MintSignature) => {
      // eslint-disable-next-line no-console
      console.info(`Sending sign TX`)

      const {
        contractAddress,
        extensionAddress,
        mintTo,
        signature,
        uris,
        tokenNonce,
        chosenRoyaltyAddress,
        chosenBps,
        artBlocksProjectId,
        contractType,
        highlightId,
        blockchain,
        highlightChosenTokens,
        artBlocksHash,
      } = mintSignature

      const royaltyAddresses: Address[] =
        !chosenRoyaltyAddress || chosenBps === 0 ? [] : [chosenRoyaltyAddress]
      const royaltyBpss: number[] =
        !chosenBps || chosenBps === 0 ? [] : [chosenBps]

      switch (contractType) {
        case 'HIGHLIGHT':
          assert(highlightId, 'highlight vector id is missing')
          assert(extensionAddress === API.HIGHLIGHT_MINT_MANAGER[blockchain])
          assert(
            blockchain === Blockchain.ETHEREUM ||
              blockchain === Blockchain.ETHEREUM_SEPOLIA ||
              blockchain === Blockchain.FAKE_ETHEREUM_ON_SEPOLIA,
            'Currently only support highlight on Ethereum and Sepolia'
          )

          if (highlightChosenTokens !== null) {
            assert(highlightChosenTokens.length != 0)
            return await mintHighlightChooseSignableTransaction({
              mintManagerAddress: extensionAddress,
              mintTo,
              mechanicVectorId: highlightId,
              highlightChosenTokens,
              signature,
              highlightMintFee: API.HIGHLIGHT_MINT_FEE_GWEI[blockchain],
            })
          } else {
            return await mintHighlightSignableTransaction({
              mintManagerAddress: extensionAddress,
              mintTo,
              mechanicVectorId: highlightId,
              numToMint: 1,
              signature,
              highlightMintFee: API.HIGHLIGHT_MINT_FEE_GWEI[blockchain],
            })
          }
        case 'ART_BLOCKS': {
          assert(artBlocksProjectId != null, 'projectId is missing')
          const version =
            extensionAddress == API.ART_BLOCKS_INTEGRATION_ADDRESS_V1
              ? '1.0'
              : extensionAddress == API.ART_BLOCKS_INTEGRATION_ADDRESS_V2
              ? '2.0'
              : fail('invalid art blocks minter contract')
          return await mintArtblocksSignableTransaction({
            mintTo,
            projectId: artBlocksProjectId,
            coreContractAddress: contractAddress,
            minterContractAddress: extensionAddress,
            tokenNonce,
            signature,
            artBlocksHash,
            version,
          })
        }

        case 'MANIFOLD':
          assert(uris?.[0], 'URIs are missing')
          return await mintManifoldSignableTransaction({
            creatorContractAddress: contractAddress,
            extensionAddress,
            mintTo,
            uri: uris[0],
            tokenNonce,
            royaltyAddresses,
            royaltyBpss,
            signature,
          })

        case 'REGULAR':
          assert(uris?.[0], 'URIs are missing')
          return await mintSignableTransaction({
            contractAddress,
            mintTo,
            uri: uris[0],
            tokenNonce,
            royaltyAddresses,
            royaltyBpss,
            signature,
          })

        default:
          throw new Error(`Unexpected contract type ${contractType}`)
      }
    },
  })
}

const useClaimMintSignable = () => {
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState<boolean>()
  const [claimed, setClaimed] = useState<boolean>()

  const { mutateAsync: handleSignedTransaction } = useSignedTransaction()
  const { ensureBlockchain } = useConnectWallet()

  const claimSignable = useCallback(
    async (mintSignableOrderId: string, userWallet: string) => {
      setLoading(true)
      setError(undefined)

      try {
        // call mintSignature API
        const mintSignature = await API.customer.claimMintSignable(
          mintSignableOrderId,
          userWallet
        )

        const { blockchain, mintTo, uris } = mintSignature.data

        assert(
          getAddress(mintTo) === getAddress(userWallet),
          'mintTo does not match'
        )
        assert(!uris || uris.length === 1, 'uris are set but not length 1')

        await ensureBlockchain(blockchain)

        // make the transaction
        const transaction = await handleSignedTransaction(mintSignature.data)
        // eslint-disable-next-line no-console
        console.info('Got TX hash', transaction)

        await API.customer.claimMintSignableSetTransaction(
          mintSignableOrderId,
          transaction.transactionHash
        )
        setClaimed(true)
      } catch (e: any) {
        console.error('Failed to claim mint signable', e)
        if (
          e.message.includes('User rejected') ||
          e.message.includes('User denied')
        ) {
          setError('User rejected transaction')
        } else if (e.error?.code === 4001) {
          if (e.error?.message === 'A request is already in progress') {
            setError('A request is already in progress')
          }
          setError('Metamask user cancelled request')
        } else if (e.code === 'NETWORK_ERROR') {
          setError('Network Error: Please check your internet connection.')
        } else {
          console.error('Unexpected error in useClaimSignable', e)
          const axiosError = e as AxiosError
          if (
            axiosError?.response?.status === 400 &&
            isErrorData(axiosError?.response?.data)
          ) {
            setError(
              `Sorry, something went wrong. Here is some details: ${axiosError.response.data.message}`
            )
          } else {
            setError(
              `Sorry, something went wrong. Here is some details: ${
                e.message ?? e
              }`
            )
          }
        }
      } finally {
        setLoading(false)
      }
    },
    [handleSignedTransaction, ensureBlockchain]
  )

  return { claimSignable, claimed, error, loading }
}

export default useClaimMintSignable
